import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { uploadAudioFile, updateLineAudioUrl } from '../../../services/audioManagementService';

const BatchAudioUploadModal = ({ isOpen, onClose, selectedLineIds, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState([]); // Array of {lineId, fileName, status, progress, error}
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleDragEnter = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e?.dataTransfer?.files || []);
    handleFilesAdded(droppedFiles);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e?.target?.files || []);
    handleFilesAdded(selectedFiles);
  };

  const handleFilesAdded = (newFiles) => {
    // Filter for MP3 files only
    const mp3Files = newFiles?.filter(file => {
      const validTypes = ['audio/mpeg', 'audio/mp3'];
      return validTypes?.includes(file?.type);
    });

    if (mp3Files?.length === 0) {
      alert('Please select valid MP3 files only.');
      return;
    }

    // Validate file sizes (10MB max)
    const maxSize = 10 * 1024 * 1024;
    const validFiles = mp3Files?.filter(file => {
      if (file?.size > maxSize) {
        alert(`${file?.name} exceeds 10MB limit and will be skipped.`);
        return false;
      }
      return true;
    });

    setFiles(prev => [...prev, ...validFiles]);
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev?.filter((_, i) => i !== index));
  };

  const handleStartUpload = async () => {
    if (files?.length === 0) {
      alert('Please add at least one MP3 file.');
      return;
    }

    if (files?.length > selectedLineIds?.length) {
      alert(`You have ${files?.length} files but only ${selectedLineIds?.length} lines selected. Please adjust your selection.`);
      return;
    }

    setIsUploading(true);
    const statusArray = [];

    // Initialize status for each file
    files?.forEach((file, index) => {
      statusArray?.push({
        lineId: selectedLineIds?.[index],
        fileName: file?.name,
        status: 'pending',
        progress: 0,
        error: null
      });
    });

    setUploadStatus(statusArray);

    // Upload files sequentially
    for (let i = 0; i < files?.length; i++) {
      const file = files?.[i];
      const lineId = selectedLineIds?.[i];

      try {
        // Update status to uploading
        setUploadStatus(prev => prev?.map((item, idx) =>
          idx === i ? { ...item, status: 'uploading', progress: 0 } : item
        ));

        // Simulate progress updates
        const progressInterval = setInterval(() => {
          setUploadStatus(prev => prev?.map((item, idx) => {
            if (idx === i && item?.progress < 90) {
              return { ...item, progress: item?.progress + 10 };
            }
            return item;
          }));
        }, 200);

        // Upload file to storage
        const uploadResult = await uploadAudioFile(file, lineId);

        clearInterval(progressInterval);

        // Update line with audio URL
        await updateLineAudioUrl(lineId, uploadResult?.path);

        // Update status to completed
        setUploadStatus(prev => prev?.map((item, idx) =>
          idx === i ? { ...item, status: 'completed', progress: 100 } : item
        ));
      } catch (error) {
        console.error(`Error uploading ${file?.name}:`, error);
        setUploadStatus(prev => prev?.map((item, idx) =>
          idx === i ? { ...item, status: 'error', error: error?.message || 'Upload failed' } : item
        ));
      }
    }

    setIsUploading(false);

    // Check if all uploads completed successfully
    const allSuccess = statusArray?.every(item => item?.status === 'completed');
    if (allSuccess) {
      setTimeout(() => {
        onUploadComplete?.();
        handleClose();
      }, 2000);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setFiles([]);
      setUploadStatus([]);
      onClose?.();
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Icon name="Clock" size={16} className="text-muted-foreground" />;
      case 'uploading':
        return <Icon name="Upload" size={16} className="text-primary animate-pulse" />;
      case 'completed':
        return <Icon name="CheckCircle" size={16} className="text-success" />;
      case 'error':
        return <Icon name="AlertCircle" size={16} className="text-destructive" />;
      default:
        return null;
    }
  };

  const completedCount = uploadStatus?.filter(item => item?.status === 'completed')?.length;
  const errorCount = uploadStatus?.filter(item => item?.status === 'error')?.length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-card-foreground">Batch Audio Upload</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Upload MP3 files for {selectedLineIds?.length} selected line(s)
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            disabled={isUploading}
            iconName="X"
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Drag and Drop Zone */}
          {!isUploading && uploadStatus?.length === 0 && (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging
                  ? 'border-primary bg-primary/5' :'border-border bg-muted/20'
              }`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Drag & Drop MP3 Files Here
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                or click to browse your files
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/mpeg,audio/mp3"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef?.current?.click()}
                iconName="FolderOpen"
                iconPosition="left"
              >
                Browse Files
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                MP3 format only • Max 10MB per file • {selectedLineIds?.length} lines selected
              </p>
            </div>
          )}

          {/* File List */}
          {files?.length > 0 && uploadStatus?.length === 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  Files Ready ({files?.length})
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFiles([])}
                  iconName="Trash2"
                >
                  Clear All
                </Button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {files?.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-background border border-border rounded-lg"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Icon name="Music" size={20} className="text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {file?.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(file?.size / 1024)?.toFixed(2)} KB • Line {index + 1} of {selectedLineIds?.length}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFile(index)}
                      iconName="X"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploadStatus?.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  Upload Progress
                </h3>
                <div className="text-sm text-muted-foreground">
                  {completedCount} of {uploadStatus?.length} completed
                  {errorCount > 0 && (
                    <span className="text-destructive ml-2">• {errorCount} failed</span>
                  )}
                </div>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {uploadStatus?.map((item, index) => (
                  <div
                    key={index}
                    className="p-3 bg-background border border-border rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {getStatusIcon(item?.status)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {item?.fileName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Line ID: {item?.lineId}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs font-medium text-muted-foreground">
                        {item?.status === 'uploading' && `${item?.progress}%`}
                        {item?.status === 'completed' && 'Done'}
                        {item?.status === 'error' && 'Failed'}
                        {item?.status === 'pending' && 'Waiting'}
                      </span>
                    </div>
                    {item?.status === 'uploading' && (
                      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all duration-300"
                          style={{ width: `${item?.progress}%` }}
                        />
                      </div>
                    )}
                    {item?.status === 'error' && (
                      <p className="text-xs text-destructive mt-1">{item?.error}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {files?.length > 0 && uploadStatus?.length === 0 && (
              <span>
                {files?.length} file(s) ready • {selectedLineIds?.length - files?.length} line(s) remaining
              </span>
            )}
            {uploadStatus?.length > 0 && (
              <span>
                {isUploading ? 'Uploading in progress...' : 'Upload complete'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isUploading}
            >
              {uploadStatus?.length > 0 && !isUploading ? 'Close' : 'Cancel'}
            </Button>
            {uploadStatus?.length === 0 && (
              <Button
                onClick={handleStartUpload}
                disabled={files?.length === 0 || isUploading}
                loading={isUploading}
                iconName="Upload"
                iconPosition="left"
              >
                Upload {files?.length} File(s)
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchAudioUploadModal;